import React from 'react';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronRightIcon,
  DocumentChartBarIcon,
  DocumentCheckIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  PhoneArrowUpRightIcon,
  RectangleStackIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Spinner } from '@oysterjs/ui/spinner';
import { Button } from '@oysterjs/ui/button';
import { FieldGroup, Fieldset } from '@oysterjs/ui/fieldset';
import { Select } from '@oysterjs/ui/select';
import { Dialog, DialogBody, DialogTitle } from '@oysterjs/ui/dialog';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@oysterjs/ui/dropdown';
import { AnimateIn } from '@oysterjs/ui/transition';
import { ApolloError } from '@apollo/client';
import { ValidationError } from '@oysterjs/core/graphql';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { getStaticsBaseUrl } from '@oysterjs/core/config';

import {
  versionDraftEmail,
  type DraftEmailSummary
} from '@oysterjs/ais/components/workspaces/lib/draft-email-event';
import {
  emailBodyOf,
  withEmailBody
} from '@oysterjs/ais/components/workspaces/lib/email-artifact-body';
import { emailDraftPlainText } from '@oysterjs/ais/components/workspaces/lib/email-handoff-text';
import type { BulkEmailDraftCardState } from '@oysterjs/ais/components/workspaces/lib/bulk-email-draft-event';
import { pluralize } from '@oysterjs/ais/lib/pluralize';
import type {
  ApplicationFileGeneratorToolFieldsFragment,
  CaraWorkspaceFieldsFragment,
  DocxEditorToolFieldsFragment,
  XlsxEditorToolFieldsFragment,
  DraftDocumentToolInput,
  DraftOutboundCallToolInput,
  DraftOutboundCallToolOutput,
  FillTemplateToolFieldsFragment,
  HtmlDocumentTemplateFieldsFragment,
  VoiceAgentOverviewFieldsFragment
} from '@oysterjs/ais/api/graphql';
import {
  CreateOutboundCallDocument,
  GetVoiceAgentsDocument,
  OutboundCallSourceTrigger,
  VoiceAgentDirection
} from '@oysterjs/ais/api/graphql';
import { client } from '@oysterjs/ais/api/client';
import { PdfPagesViewer } from '@oysterjs/ais/components/pdf-pages-viewer';
import { usePagedDocumentsEnabled } from '@oysterjs/ais/components/use-paged-documents-enabled';
import {
  buildOutboundCallMutationInput,
  defaultScheduledTimezone,
  formatOutboundCallScheduleFromApi,
  OUTBOUND_CALL_DRAFT_VALIDATION_PATHS,
  outboundCallDraftFromTool,
  OutboundCallDraft
} from '@oysterjs/ais/components/calls/outbound-call';
import { OutboundCallFormArtifactFields } from '@oysterjs/ais/components/calls/outbound-call-form-fields';
import {
  applicationFileGeneratingLabel,
  docxEditorToolTitle,
  extractClientNameFromFillTemplateInput,
  isDocxApplicationFile
} from '@oysterjs/ais/components/workspaces/lib/utils';
import { BUILTIN_STYLES } from '@oysterjs/ais/components/workspaces/lib/artifact';
import { useArtifactVersionNumber } from '@oysterjs/ais/components/workspaces/hooks/use-artifact-version-number';
import { filterHtmlDocumentStyleRecords } from '@oysterjs/ais/components/workspaces/lib/html-document-artifact';
import { useStableLastTruthy } from '@oysterjs/ais/components/workspaces/hooks/use-stable-last-truthy';
import { useMailboxComposerEnabled } from '@oysterjs/ais/components/workspaces/hooks/use-mailbox-composer-enabled';

/**
 * Shared card chrome used by every artifact card except the legacy
 * `DocumentDraftArtifact`. Applies the standard outer `AnimateIn` (with shimmer
 * while loading), a loading-badge slot wrapped in its own `AnimateIn` to match the
 * majority style, and an interactive header gated on `onOpen`.
 */
const ArtifactCard = (props: {
  completed: boolean;
  loadingBadge?: React.ReactNode;
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  onOpen?: () => void;
  topRightSlot?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  const interactive = !!props.onOpen;
  return (
    <AnimateIn
      shimmer={!props.completed}
      className={clsx(
        'rounded-lg shadow bg-white dark:bg-neutral-800 p-4 max-w-xl relative',
        interactive &&
          'hover:shadow-lg transition-all duration-150 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500'
      )}
      onClick={interactive ? props.onOpen : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                props.onOpen?.();
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      data-conversation-role="artifact-card"
    >
      {!props.completed && props.loadingBadge && (
        <AnimateIn className="relative">{props.loadingBadge}</AnimateIn>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-row gap-2">
          {props.icon}
          <div>
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              {props.title}
            </p>
            {props.meta && (
              <p className="text-xs mt-1 text-neutral-400 dark:text-neutral-500">{props.meta}</p>
            )}
            {props.subtitle && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                {props.subtitle}
              </p>
            )}
          </div>
        </div>
        {props.topRightSlot ??
          (interactive && (
            <ChevronRightIcon className="size-4 text-neutral-400 dark:text-neutral-500" />
          ))}
      </div>
      {props.children}
    </AnimateIn>
  );
};

/**
 * Placeholder shown for the brief window between an artifact tool starting and its
 * input streaming in (e.g. `event.toolCall.draftEmailTool.input` is still null).
 * Wraps the same `ArtifactCard` chrome with `completed={false}` so the shimmer
 * affordance is identical to the real card's loading state — the placeholder
 * visually morphs into the populated card as soon as input arrives.
 */
export const ArtifactPlaceholder = (props: {
  title: string;
  icon?: React.ReactNode;
  loadingBadge?: React.ReactNode;
}) => (
  <ArtifactCard
    completed={false}
    loadingBadge={props.loadingBadge}
    title={props.title}
    icon={props.icon}
  />
);

export const ArtifactFailure = (props: { title: string; message: string }) => (
  <ArtifactCard
    completed
    title={props.title}
    subtitle={<span className="text-red-600 dark:text-red-400">{props.message}</span>}
  />
);

/**
 * Hands the draft to whatever mail client the desktop opens `mailto:` with —
 * Outlook, for the agencies this is offered to. The whole body rides in the
 * URL, so what arrives is plain text with no signature, no attachments and no
 * send record: acceptable only because the alternative, for an agency without
 * the mailbox feature, is retyping the email by hand.
 *
 * Kept off the card wherever Cara can send for itself, which is the whole
 * point of the mailbox: a send the ledger never saw is a send nobody can
 * account for afterwards.
 */
const openInEmailClient = (draft: DraftEmailSummary, body: string) => {
  window.open(mailtoHref(draft, body), '_blank');
};

const mailtoHref = (draft: DraftEmailSummary, body: string): string => {
  // The comma between addresses is what makes them several, so each one is
  // encoded and the separators are left alone.
  const to = draft.recipients
    .split(',')
    .map((address) => encodeURIComponent(address.trim()))
    .filter(Boolean)
    .join(',');
  const params = new URLSearchParams();
  if (draft.subject) params.set('subject', draft.subject);
  if (body) params.set('body', body);
  // URLSearchParams spells a space '+', which a mail client shows literally in
  // a subject line and a body.
  const query = params.toString().replace(/\+/g, '%20');
  return `mailto:${to}${query ? `?${query}` : ''}`;
};

/**
 * Only artifact-backed drafts with an accessible mailbox offer the composer.
 * Legacy drafts and people without mailboxes keep the inline letter and its
 * two handoff actions. Both render text, never model-written HTML or CSS.
 */
export const EmailDraftArtifact = (props: {
  draft: DraftEmailSummary;
  artifact?: CaraWorkspaceFieldsFragment['artifacts'][0];
  /**
   * Version this call wrote, from the tool output. Used for the card label when
   * the matching artifact row has not arrived on the workspace tick yet.
   */
  version?: number;
  completed: boolean;
  /** True when this card is an UPDATE_DRAFT revision rather than a first compose. */
  updating?: boolean;
  setShowArtifactFocusDialog: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
}) => {
  const { artifact, setShowArtifactFocusDialog } = props;
  // Cara sends its own mail once the mailbox is on, and the composer's Send is
  // the only path that records one. The handoff is the fallback for agencies
  // that have no such path at all -- people with no mailbox keep the inline
  // letter and its two handoff actions rather than an editor they cannot send
  // from.
  //
  // A draft still being written has no artifact to open yet, and the card stays
  // inert rather than opening an empty composer.
  const mailboxEnabled = useMailboxComposerEnabled() && !!artifact;
  const draft = versionDraftEmail(props.draft, artifact);
  // Seed the tool-call body when the tick emptied it; the network fetch then
  // lands the stored body and contentRevision. The card records one revision but
  // opening always lands on the newest one: useArtifactFocus funnels every
  // entrypoint through newestArtifactVersion, so no caller names the target.

  const onOpen =
    artifact && mailboxEnabled
      ? () => {
          const seeded =
            draft.bodyHtml && emailBodyOf(artifact) === undefined
              ? withEmailBody(artifact, draft.bodyHtml)
              : artifact;
          setShowArtifactFocusDialog(seeded);
        }
      : undefined;
  const { bodyHtml, bodyText } = draft;
  const body = React.useMemo(
    () => (mailboxEnabled ? '' : emailDraftPlainText({ bodyHtml, bodyText })),
    [mailboxEnabled, bodyHtml, bodyText]
  );
  const [copyStatus, setCopyStatus] = React.useState<'idle' | 'copied' | 'error'>('idle');
  React.useEffect(() => setCopyStatus('idle'), [body]);
  React.useEffect(() => {
    if (copyStatus !== 'copied') return;
    const timer = setTimeout(() => setCopyStatus('idle'), 2000);
    return () => clearTimeout(timer);
  }, [copyStatus]);

  const copyAsPlainText = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('error');
    }
  };

  const displayVersion = artifact?.version ?? props.version;
  const meta = displayVersion
    ? `Version ${displayVersion} (${draft.bodyText.length} characters)`
    : `Wrote ${draft.bodyText.length} characters...`;

  return (
    <ArtifactCard
      completed={props.completed}
      loadingBadge={
        <Badge color="blue" className="mb-2">
          {props.updating ? 'Updating email' : 'Drafting email'}
        </Badge>
      }
      icon={<EnvelopeIcon className="size-6 text-neutral-500 dark:text-neutral-400" />}
      title={draft.subject}
      meta={meta}
      subtitle={draft.recipients}
      onOpen={onOpen}
      topRightSlot={
        mailboxEnabled || !props.completed ? undefined : (
          // Stops the click reaching the card, which would open the composer
          // behind the menu.
          <div onClick={(event) => event.stopPropagation()}>
            <Dropdown>
              <DropdownButton plain aria-label="Email draft actions">
                <EllipsisVerticalIcon className="size-5" />
              </DropdownButton>
              <DropdownMenu anchor="bottom end">
                <DropdownItem onClick={() => void copyAsPlainText()}>
                  {copyStatus === 'copied' ? (
                    <CheckIcon data-slot="icon" />
                  ) : (
                    <ClipboardDocumentIcon data-slot="icon" />
                  )}
                  Copy as plain text
                </DropdownItem>
                <DropdownItem onClick={() => openInEmailClient(draft, body)}>
                  <EnvelopeIcon data-slot="icon" />
                  Open in email client
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )
      }
    >
      {!mailboxEnabled && (
        <>
          {copyStatus === 'copied' && (
            <p role="status" className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
              Copied
            </p>
          )}
          {copyStatus === 'error' && (
            <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
              Couldn't copy. Select the email text below and copy it manually.
            </p>
          )}
          <pre
            aria-label="Email body"
            className="text-sm font-body mt-4 text-neutral-700 dark:text-neutral-300 whitespace-break-spaces break-words"
          >
            {body}
          </pre>
        </>
      )}
    </ArtifactCard>
  );
};

const bulkEmailDraftSummary = ({ drafted, failed }: { drafted: number; failed: number }) => {
  if (drafted + failed === 0) return undefined;
  const draftedLine = `${drafted} ${pluralize(drafted, 'email')} drafted.`;
  return failed > 0 ? `${draftedLine} ${failed} couldn't be drafted.` : draftedLine;
};

/**
 * A bulk draft Cara made or changed. The agent checks its emails and sends them on its page in
 * Mailbox, so they show there rather than as cards of their own.
 */
export const BulkEmailDraftArtifact = (props: {
  /** Undefined until the bulk draft exists. */
  name?: string;
  recipientCount?: number;
  state: BulkEmailDraftCardState;
  onOpen?: () => void;
}) => {
  const { state, recipientCount } = props;
  if (state.kind === 'notCreated') {
    return (
      <ArtifactFailure
        title="Bulk draft"
        message="Cara couldn't create this bulk draft. Ask Cara to try again."
      />
    );
  }
  const isWorking = state.kind === 'creating' || state.kind === 'drafting';
  return (
    <ArtifactCard
      completed={!isWorking}
      loadingBadge={
        <Badge role="status" color="blue" className="mb-2">
          <Spinner size="size-3.5" color="blue" />
          {state.kind === 'drafting'
            ? `Drafting ${state.finished} of ${state.total}`
            : 'Creating bulk draft'}
        </Badge>
      }
      icon={<RectangleStackIcon className="size-6 text-neutral-500 dark:text-neutral-400" />}
      title={props.name ?? 'Bulk draft'}
      meta={
        recipientCount === undefined
          ? undefined
          : `Bulk draft · ${recipientCount} ${pluralize(recipientCount, 'recipient')}`
      }
      subtitle={state.kind === 'settled' ? bulkEmailDraftSummary(state) : undefined}
      onOpen={props.onOpen}
    />
  );
};

export const DocumentDraftArtifact = (props: {
  workspaceId: string;
  document: DraftDocumentToolInput;
  htmlDocumentTemplates: HtmlDocumentTemplateFieldsFragment[];
  completed: boolean;
}) => {
  const pagedDocuments = usePagedDocumentsEnabled();
  const [showHtmlDocument, setShowHtmlDocument] = React.useState(false);
  const [selectedStyleId, setSelectedStyleId] = React.useState<string>('');
  const customStyles = React.useMemo(
    () => filterHtmlDocumentStyleRecords(props.htmlDocumentTemplates),
    [props.htmlDocumentTemplates]
  );
  return (
    <>
      <Dialog open={showHtmlDocument} onClose={() => setShowHtmlDocument(false)} size="4xl">
        <DialogTitle>{props.document.title}</DialogTitle>
        <DialogBody>
          {/* With designs on, the document's design decides its look, set from the Design menu. */}
          {!pagedDocuments && (
            <Select
              value={selectedStyleId}
              onChange={(e) => setSelectedStyleId(e.target.value)}
              className="max-w-80"
            >
              <option value="">None</option>
              <optgroup label="Built-in styles">
                {BUILTIN_STYLES.map((style) => (
                  <option key={style.styleId} value={style.styleId}>
                    {style.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Custom styles">
                {customStyles.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.title}
                  </option>
                ))}
                {customStyles.length === 0 && <option disabled>No custom styles found</option>}
              </optgroup>
            </Select>
          )}
          {pagedDocuments ? (
            <div className="h-[calc(100vh-16rem)] w-full flex flex-col">
              <PdfPagesViewer
                key={selectedStyleId}
                src={`${getStaticsBaseUrl()}/agency/html/${props.workspaceId}/${props.document.artifactId}?style_id=${selectedStyleId}`}
                title={props.document.title}
              />
            </div>
          ) : (
            <iframe
              src={`${getStaticsBaseUrl()}/agency/html/${props.workspaceId}/${props.document.artifactId}?style_id=${selectedStyleId}`}
              className="h-[calc(100vh-16rem)] w-full mt-4 rounded-lg bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-700"
              title={props.document.title}
            />
          )}
        </DialogBody>
      </Dialog>
      <AnimateIn
        shimmer={!props.completed}
        className="rounded-lg shadow bg-white dark:bg-neutral-800 p-4 max-w-xl relative"
        data-conversation-role="artifact-card"
      >
        {!props.completed && (
          <AnimateIn className="relative">
            <Badge color="blue" className="mb-4">
              Drafting document
            </Badge>
          </AnimateIn>
        )}
        <div className="flex justify-between gap-4">
          <div className="flex flex-row gap-2">
            <PaperClipIcon className="size-6 text-neutral-500 dark:text-neutral-400" />
            <div>
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {props.document.title}
              </p>
            </div>
          </div>
          <Button
            plain
            target="_blank"
            onClick={() => setShowHtmlDocument(true)}
            className="flex-none"
          >
            Preview Document
          </Button>
        </div>
      </AnimateIn>
    </>
  );
};

export const FillTemplateArtifact = (props: {
  workspaceId: string;
  toolCall: FillTemplateToolFieldsFragment;
  artifact?: CaraWorkspaceFieldsFragment['artifacts'][0];
  completed: boolean;
  setShowArtifactFocusDialog: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
}) => {
  const artifactVersionNumber = useArtifactVersionNumber();
  // Streaming subscription updates can momentarily emit input without a recognizable
  // client-name field; preserve the last-known good name to avoid flicker.
  const currentClientName =
    props.artifact?.templateForm?.label ||
    extractClientNameFromFillTemplateInput(props.toolCall.input);
  const displayClientName = useStableLastTruthy(currentClientName);

  const artifact = props.artifact;
  const onOpen =
    artifact && props.completed ? () => props.setShowArtifactFocusDialog(artifact) : undefined;

  return (
    <ArtifactCard
      completed={props.completed}
      loadingBadge={
        <Badge color="blue" className="mb-4">
          Filling template
        </Badge>
      }
      icon={<PaperClipIcon className="size-6 text-neutral-500 dark:text-neutral-400" />}
      title={
        (artifact?.templateForm as { title?: string } | undefined)?.title ||
        props.toolCall.input.templateId
      }
      meta={artifact ? `Version ${artifactVersionNumber(artifact)}` : undefined}
      subtitle={displayClientName || undefined}
      onOpen={onOpen}
    />
  );
};

export const FileEditorArtifact = (props: {
  artifact?: CaraWorkspaceFieldsFragment['artifacts'][0];
  title?: string;
  contentLength: number;
  /** The call handed a writer a brief, and the document it writes is not in the workspace. */
  awaitingDocument?: boolean;
  /** The document's newest version; the card opens the version this call wrote, not this one. */
  latestArtifact?: CaraWorkspaceFieldsFragment['artifacts'][0];
  completed: boolean;
  setShowArtifactFocusDialog: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
}) => {
  const artifactVersionNumber = useArtifactVersionNumber();
  const paged = usePagedDocumentsEnabled();
  const artifact = props.artifact;
  const onOpen = artifact ? () => props.setShowArtifactFocusDialog(artifact) : undefined;
  const latest = props.latestArtifact;
  const newer =
    paged && artifact && latest && latest.version > artifact.version
      ? ` · latest is v${artifactVersionNumber(latest)}`
      : '';
  const meta = artifact
    ? `Version ${artifactVersionNumber(artifact)} (${props.contentLength} characters)${newer}`
    : props.awaitingDocument
      ? props.completed
        ? undefined
        : 'Writing document...'
      : `Wrote ${props.contentLength} characters...`;

  return (
    <ArtifactCard
      completed={props.completed}
      loadingBadge={
        <Badge color="blue" className="mb-4">
          {props.contentLength === 0 ? 'Planning document' : 'Drafting document'}
        </Badge>
      }
      icon={<DocumentTextIcon className="size-6 text-neutral-500 dark:text-neutral-400" />}
      title={props.title || artifact?.document?.title || '(Untitled document)'}
      meta={meta}
      onOpen={onOpen}
    />
  );
};

export const ApplicationFileArtifact = (props: {
  workspaceId: string;
  artifact?: CaraWorkspaceFieldsFragment['artifacts'][0];
  toolCall?: ApplicationFileGeneratorToolFieldsFragment;
  completed: boolean;
  setShowArtifactFocusDialog?: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
}) => {
  const artifactVersionNumber = useArtifactVersionNumber();
  const title =
    props.artifact?.applicationFile?.title || props.toolCall?.input?.title || 'Spreadsheet';
  const artifact = props.artifact;
  const setShow = props.setShowArtifactFocusDialog;
  const onOpen = artifact && setShow ? () => setShow(artifact) : undefined;

  return (
    <ArtifactCard
      completed={props.completed}
      loadingBadge={
        <Badge color="blue" className="mb-4">
          Drafting{' '}
          {applicationFileGeneratingLabel(
            isDocxApplicationFile({ toolCall: props.toolCall ?? undefined }),
            false
          )}
        </Badge>
      }
      icon={
        isDocxApplicationFile({ toolCall: props.toolCall ?? undefined }) ? (
          <DocumentTextIcon className="size-6 text-neutral-500 dark:text-neutral-400" />
        ) : (
          <DocumentChartBarIcon className="size-6 text-neutral-500 dark:text-neutral-400" />
        )
      }
      title={title}
      meta={artifact ? `Version ${artifactVersionNumber(artifact)}` : undefined}
      onOpen={onOpen}
    />
  );
};

export const DocxEditorArtifact = (props: {
  workspaceId: string;
  artifact?: CaraWorkspaceFieldsFragment['artifacts'][0];
  toolCall?: DocxEditorToolFieldsFragment;
  completed: boolean;
  setShowArtifactFocusDialog?: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
}) => {
  const artifactVersionNumber = useArtifactVersionNumber();
  const title =
    props.artifact?.applicationFile?.title ||
    docxEditorToolTitle(props.toolCall) ||
    'Word document';
  const artifact = props.artifact;
  const setShow = props.setShowArtifactFocusDialog;
  const onOpen = artifact && setShow ? () => setShow(artifact) : undefined;

  return (
    <ArtifactCard
      completed={props.completed}
      loadingBadge={
        <Badge color="blue" className="mb-4">
          Drafting {applicationFileGeneratingLabel(true, false)}
        </Badge>
      }
      icon={<DocumentTextIcon className="size-6 text-neutral-500 dark:text-neutral-400" />}
      title={title}
      meta={artifact ? `Version ${artifactVersionNumber(artifact)}` : undefined}
      onOpen={onOpen}
    />
  );
};

export const XlsxEditorArtifact = (props: {
  artifact?: CaraWorkspaceFieldsFragment['artifacts'][0];
  toolCall?: XlsxEditorToolFieldsFragment;
  completed: boolean;
  setShowArtifactFocusDialog?: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
}) => {
  const artifactVersionNumber = useArtifactVersionNumber();
  const title =
    props.artifact?.applicationFile?.title || props.toolCall?.input?.title?.trim() || 'Spreadsheet';
  const artifact = props.artifact;
  const setShow = props.setShowArtifactFocusDialog;
  const onOpen = artifact && setShow ? () => setShow(artifact) : undefined;

  return (
    <ArtifactCard
      completed={props.completed}
      loadingBadge={
        <Badge color="blue" className="mb-4">
          Filling {applicationFileGeneratingLabel(false, false)}
        </Badge>
      }
      icon={<TableCellsIcon className="size-6 text-neutral-500 dark:text-neutral-400" />}
      title={title}
      meta={artifact ? `Version ${artifactVersionNumber(artifact)}` : undefined}
      onOpen={onOpen}
    />
  );
};

export const PDFEditorArtifact = (props: {
  artifact?: CaraWorkspaceFieldsFragment['artifacts'][0];
  fileName: string;
  formTitle?: string;
  /** Who the form was filled for, shown under the form name. */
  subtitle?: string;
  completed: boolean;
  setShowArtifactFocusDialog: (artifact: CaraWorkspaceFieldsFragment['artifacts'][0]) => void;
}) => {
  const artifactVersionNumber = useArtifactVersionNumber();
  const [showFileEditor, setShowFileEditor] = React.useState(false);
  const [initialCompleted] = React.useState(props.completed);

  const { artifact, completed, setShowArtifactFocusDialog } = props;

  React.useEffect(() => {
    if (completed && !initialCompleted && !showFileEditor && artifact) {
      setShowFileEditor(true);
      setShowArtifactFocusDialog(artifact);
    }
  }, [completed, initialCompleted, showFileEditor, artifact, setShowArtifactFocusDialog]);

  const onOpen = artifact ? () => setShowArtifactFocusDialog(artifact) : undefined;

  return (
    <ArtifactCard
      completed={completed}
      loadingBadge={
        <Badge color="blue" className="mb-4">
          {props.formTitle ? `Filling ${props.formTitle}` : 'Filling PDF form'}
        </Badge>
      }
      icon={<DocumentCheckIcon className="size-6 text-neutral-500 dark:text-neutral-400" />}
      title={props.fileName}
      meta={artifact ? `Version ${artifactVersionNumber(artifact)}` : undefined}
      subtitle={props.subtitle}
      onOpen={onOpen}
    />
  );
};

type ConfirmedCall = { id: string; scheduledAt: string | null; timezone: string };

const confirmedCallFromToolOutput = (
  output?: DraftOutboundCallToolOutput | null
): ConfirmedCall | null => {
  if (!output?.outboundCallId) {
    return null;
  }
  const scheduledAt = output.scheduledAt;
  return {
    id: output.outboundCallId,
    scheduledAt: scheduledAt
      ? typeof scheduledAt === 'string'
        ? scheduledAt
        : new Date(scheduledAt).toISOString()
      : null,
    timezone: defaultScheduledTimezone()
  };
};

export const OutboundCallDraftArtifact = (props: {
  draft: DraftOutboundCallToolInput;
  /** Tool output — when outboundCallId is set, the draft was already confirmed. */
  output?: DraftOutboundCallToolOutput | null;
  completed: boolean;
  /** Workspace that produced this draft — stored as create-time provenance (COPILOT). */
  workspaceId: string;
}) => {
  const [draft, setDraft] = React.useState<OutboundCallDraft>(() =>
    outboundCallDraftFromTool(props.draft)
  );
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const { updateFormMulti } = useFormActions(setDraft, setValidationErrors);
  // Streaming tool args start sparse. Keep hydrating from props until the user edits.
  const [userEdited, setUserEdited] = React.useState(false);

  React.useEffect(() => {
    if (userEdited) {
      return;
    }
    setDraft((prev) => {
      const next = outboundCallDraftFromTool(props.draft);
      // Preserve an auto-selected voice agent when the tool left it blank.
      if (!next.voiceAgentId && prev.voiceAgentId) {
        return { ...next, voiceAgentId: prev.voiceAgentId };
      }
      return next;
    });
  }, [props.draft, userEdited]);

  const updateDraft = (patch: Partial<OutboundCallDraft>) => {
    setUserEdited(true);
    const fieldPaths = (Object.keys(patch) as (keyof OutboundCallDraft)[])
      .map((key) => OUTBOUND_CALL_DRAFT_VALIDATION_PATHS[key])
      .filter((path): path is string[] => !!path);
    updateFormMulti(fieldPaths, (prev) => ({ ...prev, ...patch }));
  };

  const [agents, setAgents] = React.useState<VoiceAgentOverviewFieldsFragment[]>([]);
  const [loadingAgents, setLoadingAgents] = React.useState(true);
  const [agentsError, setAgentsError] = React.useState<string | null>(null);

  const [confirming, setConfirming] = React.useState(false);
  const [confirmedCall, setConfirmedCall] = React.useState<ConfirmedCall | null>(() =>
    confirmedCallFromToolOutput(props.output)
  );
  const [confirmError, setConfirmError] = React.useState<string | null>(null);

  // Rehydrate confirmed UI when workspace subscription delivers linked tool output
  // (or on remount after refresh).
  React.useEffect(() => {
    const fromOutput = confirmedCallFromToolOutput(props.output);
    if (fromOutput) {
      setConfirmedCall(fromOutput);
    }
  }, [props.output?.outboundCallId, props.output?.scheduledAt]);

  // Deeplink support: scroll to this draft when
  // the URL contains ?confirm_call=<this artifactId>
  const selfRef = React.useRef<HTMLDivElement>(null);
  const [, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    // Read from the URL once on mount — do not re-run on every searchParams change.
    const params = new URLSearchParams(window.location.search);
    if (params.get('confirm_call') !== props.draft.artifactId) return;

    selfRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Remove the param so a refresh doesn't re-trigger
    params.delete('confirm_call');
    setSearchParams(params, { replace: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setAgentsError(null);
    client
      .query({
        query: GetVoiceAgentsDocument,
        variables: { filter: { direction: VoiceAgentDirection.Outbound } },
        fetchPolicy: 'network-only'
      })
      .then((result) => {
        const list = result.data?.voiceAgents ?? [];
        setAgents(list);
        setLoadingAgents(false);
        if (!draft.voiceAgentId && list.length === 1) {
          // Do not mark userEdited — streaming tool fields may still arrive.
          setDraft((prev) => (prev.voiceAgentId ? prev : { ...prev, voiceAgentId: list[0].id }));
        }
      })
      .catch(() => {
        setAgentsError('Unable to load outbound voice agents. Please try again.');
        setLoadingAgents(false);
      });
  }, []);

  const handleConfirm = async () => {
    if (loadingAgents || agentsError || agents.length === 0) {
      return;
    }
    setConfirming(true);
    setConfirmError(null);
    setValidationErrors([]);
    try {
      const result = await client.mutate({
        mutation: CreateOutboundCallDocument,
        variables: {
          input: {
            ...buildOutboundCallMutationInput(draft),
            source: {
              trigger: OutboundCallSourceTrigger.Copilot,
              workspaceId: props.workspaceId,
              draftArtifactId: props.draft.artifactId ?? undefined
            }
          }
        }
      });

      const applicationError = getApplicationError(
        new ApolloError({ graphQLErrors: result.errors })
      );
      const mutationValidationErrors = getValidationErrors(
        new ApolloError({ graphQLErrors: result.errors })
      );

      if (applicationError) {
        setConfirmError(applicationError.message);
        return;
      }
      if (mutationValidationErrors?.length) {
        setValidationErrors(mutationValidationErrors);
        return;
      }
      if (result.errors?.length) {
        setConfirmError(result.errors[0].message);
        return;
      }

      const created = result.data?.createOutboundCall;
      if (created) {
        setConfirmedCall({
          id: created.id,
          scheduledAt: created.schedule?.scheduledAt
            ? typeof created.schedule.scheduledAt === 'string'
              ? created.schedule.scheduledAt
              : new Date(created.schedule.scheduledAt).toISOString()
            : null,
          timezone: created.schedule?.timezone ?? draft.scheduledTimezone
        });
      }
    } catch {
      setConfirmError('Failed to schedule the call. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  if (confirmedCall) {
    return (
      <div data-conversation-role="artifact-card" ref={selfRef}>
        <AnimateIn className="rounded-lg shadow bg-white dark:bg-neutral-800 p-4 max-w-xl">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="size-6 text-green-500 dark:text-green-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {confirmedCall.scheduledAt ? 'Call scheduled' : 'Call queued'}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                {confirmedCall.scheduledAt
                  ? `Scheduled for ${formatOutboundCallScheduleFromApi({
                      schedule: {
                        scheduledAt: new Date(confirmedCall.scheduledAt),
                        timezone: confirmedCall.timezone
                      },
                      createdAt: new Date(confirmedCall.scheduledAt)
                    } as Parameters<typeof formatOutboundCallScheduleFromApi>[0])}`
                  : 'The call will be placed immediately.'}
              </p>
              <a
                href={`/app/calls/outbound/${confirmedCall.id}`}
                className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                target="_blank"
                rel="noreferrer"
              >
                Check status in Calls
                <ArrowTopRightOnSquareIcon className="size-3.5" />
              </a>
            </div>
          </div>
        </AnimateIn>
      </div>
    );
  }

  return (
    <div data-conversation-role="artifact-card" ref={selfRef}>
      <AnimateIn
        shimmer={!props.completed}
        className="rounded-lg shadow bg-white dark:bg-neutral-800 p-4 max-w-xl relative"
      >
        {!props.completed && (
          <AnimateIn className="relative">
            <Badge color="blue" className="mb-3">
              Drafting call
            </Badge>
          </AnimateIn>
        )}

        <div className="flex items-center gap-2 mb-4">
          <PhoneArrowUpRightIcon className="size-5 text-sky-500 dark:text-sky-400 shrink-0" />
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Outbound Call
          </p>
        </div>

        {agentsError && (
          <div className="mb-4 rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3 text-xs text-red-700 dark:text-red-300">
            {agentsError}
          </div>
        )}

        {!loadingAgents && !agentsError && agents.length === 0 && (
          <div className="flex items-start gap-2 mb-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-md">
            <ExclamationTriangleIcon className="size-4 text-amber-500 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-700 dark:text-amber-300">
              No outbound voice agents found.{' '}
              <a href="/app/settings/voice/new" className="font-medium underline">
                Create an outbound agent
              </a>{' '}
              to enable this feature.
            </div>
          </div>
        )}

        <Fieldset disabled={!props.completed}>
          <OutboundCallFormArtifactFields
            draft={draft}
            agents={agents}
            onDraftChange={updateDraft}
            validationErrors={validationErrors}
            showSchedule
            layout="compact"
            agentsLoading={loadingAgents}
          />
          <FieldGroup>
            {confirmError && (
              <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-900 dark:text-red-200">
                {confirmError}
              </div>
            )}

            <Button
              color="sky"
              className="w-full"
              onClick={handleConfirm}
              disabled={
                !props.completed ||
                confirming ||
                loadingAgents ||
                !!agentsError ||
                agents.length === 0
              }
            >
              {confirming ? 'Submitting…' : draft.scheduleLater ? 'Schedule Call' : 'Place Call'}
            </Button>
          </FieldGroup>
        </Fieldset>
      </AnimateIn>
    </div>
  );
};
